import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import {
  Box,
  ContentTemplate,
  CustomSelect,
  Divider,
  ErrorInfo,
  HeaderTemplate,
  SectionHeader,
  Stepper,
  TitleHeader,
  Typography,
} from 'web-library';
import { useHistory, useParams } from 'react-router-dom';
import { UploadedFile } from 'web-library/models/UploadedFile';
import RouteKey from '../../../routes/key';
import { useGetWithdrawalDropDown } from '../../../api/candidate/exam/@candidate/queries-services/queries';
import useSetUploadButtonStatus from '../../../hooks/useSetUploadButtonStatus';
import { attachedFilesService } from '../../../api';
import {
  GetWithdrawalDraftResponse,
  WithdrawalReason as WithdrawalReasonModel,
} from '../../../api/candidate/exam/model';
import { ApplicationType, UploadDocumentType } from '../../../api/candidate/config/model';
import { prepareFiles } from '../../../utils/UploadUtil';
import WithdrawalReasonAdditionalText from '../../../modules/withdrawal/WithdrawalReasonAdditionalText';
import UploadDocuments from '../../accessArrangement/SelectCondition/components/UploadDocuments';
import WithdrawalReasonDocumentInfo from '../../../modules/withdrawal/WithdrawalReasonDocumentInfo';
import WithdrawalReasonButtons from '../../../modules/withdrawal/WithdrawalReasonButtons';
import { withdrawalTypeData } from '../../../modules/admin/logic/dropDownOption';
import usePostPresignLinks from '../../../modules/withdrawal/logic/usePostPresignLinks';
import { useGetNonNationalWithdrawalDraft } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalWithdrawalDraft';
import { usePutNonNationalUpdateWithdrawalDetails } from '../../../api-refactored/non-national-exam-registration/usePutNonNationalUpdateWithdrawalDetails';
import { usePostValidateSupportingDocumentFileSize } from '../../../api-refactored/non-national-exam-registration/usePostValidateSupportingDocumentFileSize';

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      left: '-15%',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-14%',
    },
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-13%',
    },
    [theme.breakpoints.down('sm')]: {
      left: '-6%',
    },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  marginBottom: {
    marginBottom: theme.spacing(5),
  },
  divider: {
    marginTop: theme.spacing(2),
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

const nonNationalRouteSteps = [
  {
    label: 'Reason for Withdrawal',
  },
  {
    label: 'Review Details',
  },
];

const { examRegistrations: withdrawalRoute, nonNationalReview } = RouteKey.withdrawal.child;
const { withdrawal, admin } = RouteKey.child;

const { examRegistrations } = RouteKey.exam.child;

const { nonNationalInstruction } = RouteKey.withdrawal.child;

const { withdrawal: adminWithdrawal, nonNationalExamApplication, summary } = RouteKey.admin.child;

interface WithdrawalReasonProps {
  routeSteps?: { label: string }[];
  isOpsAdmin?: boolean;
}

export const WithdrawalReasonTitleHeader = ({
  routeSteps = nonNationalRouteSteps,
  isOpsAdmin = false,
}: WithdrawalReasonProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  if (isOpsAdmin) {
    return (
      <>
        <TitleHeader title={t('exam->>NonNationalExam.ReviewApplication')} />
        {routeSteps && (
          <HeaderTemplate>
            <Stepper className={classes.stepper} activeStep={0} steps={routeSteps} />
          </HeaderTemplate>
        )}
      </>
    );
  }

  return (
    <>
      <TitleHeader title={t('withdrawal->>WithdrawFromExam.ExamWithdrawal')} />
      <HeaderTemplate>
        <Stepper className={classes.stepper} activeStep={0} steps={routeSteps} />
      </HeaderTemplate>
    </>
  );
};

const getWithdrawalDraftFunction = (
  res: GetWithdrawalDraftResponse,
  setSelectReason: React.Dispatch<React.SetStateAction<string>>,
  setTextArea: React.Dispatch<React.SetStateAction<string>>,
  setDraftFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>,
  setIsFetchedDraft: React.Dispatch<React.SetStateAction<boolean>>,
  setLockedByUser: React.Dispatch<React.SetStateAction<string>>
) => {
  if (res.reason?.reasonDescription) {
    setSelectReason(res.reason.reasonDescription);
  }
  if (res.specifiedReason) {
    setTextArea(res.specifiedReason);
  }
  if (res.fileEntityModelList) {
    const filesDraft = res.fileEntityModelList?.map((draftFile) => {
      const newFile = new File([new ArrayBuffer(draftFile.fileSize)], draftFile.fileName);
      return {
        file: newFile,
        hasError: false,
        pending: false,
        uploaded: true,
        key: draftFile.uuid,
      };
    });
    setDraftFiles(filesDraft);
  }
  setIsFetchedDraft(true);

  if (res.lockedByUser) {
    setLockedByUser(res.lockedByUser);
  }
};

const WithdrawalReason = ({
  routeSteps = nonNationalRouteSteps,
  isOpsAdmin = false,
}: WithdrawalReasonProps) => {
  const { t } = useTranslation();

  const { uuid: applicationUUID } = useParams<{ uuid: string }>();
  const history = useHistory();

  const [isFetchedDraft, setIsFetchedDraft] = useState<boolean>(false);
  const [selectReason, setSelectReason] = useState<string>('');
  const [selectedArr, setSelectedArr] = useState<WithdrawalReasonModel>();
  const [textArea, setTextArea] = useState<string>('');
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [displayErrors, setDisplayErrors] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<string[]>();
  const [errorHelperText, setErrorHelperText] = useState<string>('');
  const [lockedByUser, setLockedByUser] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isFileRejected, setIsFileRejected] = useState(false);
  const [fileRejectedErrorMessage, setFileRejectedErrorMessage] = useState('');

  const { classes } = useStyles();

  const { refetch: getWithdrawalDraft } = useGetNonNationalWithdrawalDraft({
    uuid: applicationUUID,
    onSuccess: (res: GetWithdrawalDraftResponse) => {
      getWithdrawalDraftFunction(
        res,
        setSelectReason,
        setTextArea,
        setDraftFiles,
        setIsFetchedDraft,
        setLockedByUser
      );
    },
    onError: () => history.replace('/error/generic'),
  });

  const { data: dropdownData } = useGetWithdrawalDropDown(() => getWithdrawalDraft());

  const { mutate: deleteFileMutate } = attachedFilesService.all.useDeleteAppealArrangementFile(
    () => null
  );

  const handleGoToReview = useCallback(() => {
    if (isOpsAdmin) {
      history.push(
        `/${admin}/${nonNationalExamApplication.route}/${applicationUUID}/${adminWithdrawal.route}/${summary.route}`
      );
    } else {
      history.push(
        `/${withdrawal}/${withdrawalRoute.route}/${applicationUUID}/${nonNationalReview.route}`
      );
    }
  }, [isOpsAdmin, history, applicationUUID]);

  const mutatePostValidateSupportingDocumentFileSize = usePostValidateSupportingDocumentFileSize({
    onSuccess: () => {
      handleGoToReview();
    },
  });

  useSetUploadButtonStatus({
    files,
    setApiButtonStatus,
    setShouldSubmit,
    shouldSubmit,
    onSuccessCallback: () => {
      const fileKey = files.map((f: UploadedFile) => f.key);
      mutatePostValidateSupportingDocumentFileSize.mutate({ uuidList: fileKey });
      setShouldSubmit(false);
    },
  });

  useEffect(() => {
    const allFileNames = [...files, ...draftFiles]
      .map((f) => f.file.name.trim().toLowerCase());

    const hasDuplicate =
      new Set(allFileNames).size !== allFileNames.length;

    setIsFileRejected(hasDuplicate);
    setFileRejectedErrorMessage(
      hasDuplicate
        ? t('exam->>UploadDocument.DuplicateFileNames')
        : ''
    );
  }, [files, draftFiles, t]);

  useEffect(() => {
    if (dropdownData && selectReason) {
      setSelectedArr(dropdownData.find((value) => value.reasonDescription === selectReason));
    }
  }, [dropdownData, selectReason]);

  const usePostPresignLinksMutation = usePostPresignLinks({
    files,
    setFiles,
    setShouldSubmit,
    setApiButtonStatus,
    setErrorMsg,
  });

  const putReviewDetails = usePutNonNationalUpdateWithdrawalDetails({
    uuid: applicationUUID,
    onSuccess: () => {
      if (files && files.length > 0) {
        const { filenames } = prepareFiles(files, setFiles);
        usePostPresignLinksMutation.mutate({
          payload: {
            applicationType: ApplicationType.NON_NATIONAL_EXAM,
            uuid: applicationUUID,
            filenames,
            uploadDocumentType: UploadDocumentType.SUPP_DOC,
          },
        });
      } else {
        handleGoToReview();
      }
    },
    onError: () => {
      setApiButtonStatus('error');
    },
  });

  const handleSelectChange = (value: string) => {
    if (lockedByUser) {
      return;
    }

    if (files) {
      files.forEach((file) => {
        if (file.key) {
          deleteFileMutate(file.key);
        }
      });
    }

    setSelectReason(value);
    setFiles([]);
    setTextArea('');
  };

  const handleBack = () =>
    isOpsAdmin
      ? history.goBack()
      : history.push(`/${withdrawal}/${examRegistrations.route}/${nonNationalInstruction.route}`);

  const generateErrorList = () => {
    const errors = [];

    if (!selectReason) {
      setErrorHelperText(t('withdrawal->>WithdrawalReason.NoReasonSelectedError'));
    } else {
      if (
        !isOpsAdmin &&
        selectedArr?.reasonDescription !== 'Others' &&
        !files.length &&
        !draftFiles.length
      ) {
        errors.push(t('withdrawal->>WithdrawalReason.FileUploadErrorMessage'));
      }
      if (selectedArr?.additionalTextNotice && !textArea) {
        errors.push(t('withdrawal->>WithdrawalReason.SpecifiedReasonErrorMessage'));
      }
    }
    return errors;
  };

  const handleConfirm = () => {
      if (isFileRejected) {
        setDisplayErrors(true);
        return;
      }
    if (lockedByUser) {
      handleGoToReview();
      return;
    }

    const errors = generateErrorList();

    if (errors.length) {
      setDisplayErrors(true);
      setFormErrors(errors);
    }
    if (textArea && textArea.length > 200) {
      setDisplayErrors(true);
    } else if (selectedArr && errors.length === 0) {
      if (files) {
        putReviewDetails.mutate({
          payload: {
            uuid: applicationUUID,
            reason: selectedArr,
            specifiedReason: textArea,
          },
        });
      } else if (isOpsAdmin) {
        handleGoToReview();
      }
    }
  };

  return (
    <>
      <WithdrawalReasonTitleHeader routeSteps={routeSteps} isOpsAdmin={isOpsAdmin} />
      <ContentTemplate>
        <SectionHeader title={t('withdrawal->>ReviewWithdrawal.ReasonForWithdrawal')} />

        <Typography className={classes.header} variant="h3">
          {t('withdrawal->>WithdrawalReason.SelectReasonForWithdrawal')}
        </Typography>
        {dropdownData && isFetchedDraft && (
          <CustomSelect
            label={t('withdrawal->>WithdrawalReason.SelectReasonForWithdrawal')}
            placeholder={t('withdrawal->>WithdrawalReason.SelectReason')}
            helperText={errorHelperText}
            error={!selectReason && !!errorHelperText}
            displayEmpty
            value={selectReason}
            onChange={async (e) => {
              handleSelectChange(e.target.value as string);
              setErrorHelperText('');
              setSelectedArr(
                dropdownData.find((value) => value.reasonDescription === (e.target.value as string))
              );
            }}
            renderValue={() => (
              <Typography>
                {selectReason || t('withdrawal->>WithdrawalReason.SelectReason')}
              </Typography>
            )}
            menuItems={withdrawalTypeData(dropdownData)}
          />
        )}

        {selectedArr && (
          <>
            <WithdrawalReasonAdditionalText
              selectedArr={selectedArr}
              textArea={textArea}
              setTextArea={setTextArea}
              displayErrors={displayErrors}
            />

            <Divider className={classes.divider} paddingTop />

            <WithdrawalReasonDocumentInfo selectedArr={selectedArr} />

            <UploadDocuments
              files={files}
              setFiles={setFiles}
              applicationType={ApplicationType.NON_NATIONAL_EXAM}
              draftFiles={draftFiles}
              setDraftFiles={setDraftFiles}
            />
          </>
        )}
        <Box className={classes.marginBottom}>
          {lockedByUser && (
            <Typography variant="body2" className={classes.errString}>
              {t('exam->>NonNationalExam.LockedMessage', {
                lockedByUser,
              })}
            </Typography>
          )}
          <ErrorInfo label={errorMsg} />
          <WithdrawalReasonButtons
            files={files}
            apiButtonStatus={apiButtonStatus}
            textArea={textArea}
            selectReason={selectReason}
            displayErrors={displayErrors}
            setDisplayErrors={setDisplayErrors}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            handleBack={handleBack}
            handleConfirm={handleConfirm}
            isFileRejected={isFileRejected}
            fileRejectedErrorMessage={fileRejectedErrorMessage}
            isNonNational
          />
        </Box>
      </ContentTemplate>
    </>
  );
};
export default WithdrawalReason;
